import { FiEye, FiHeart, FiMessageCircle, FiShare2 } from 'react-icons/fi';
import { IoChevronForward } from 'react-icons/io5';
import { formatNumber } from '../StatBlock/StatBlock';
import './ContributionBreakdown.css';

const METRICS = [
  { key: 'views', label: 'Views', icon: <FiEye /> },
  { key: 'likes', label: 'Likes', icon: <FiHeart /> },
  { key: 'comments', label: 'Comments', icon: <FiMessageCircle /> },
  { key: 'shares', label: 'Shares', icon: <FiShare2 /> },
];

const pct = (value, total) => (total > 0 ? (value / total) * 100 : 0);

const formatPct = (p) => {
  if (p === 0) return '0%';
  if (p < 1) return '<1%';
  return `${Math.round(p)}%`;
};

/**
 * Renders a per-child contribution breakdown against a set of totals.
 *
 * Each row is one contributor (a login under an account, or an account under
 * a group). The header shows what share of each metric that contributor
 * accounts for; the bar fills to the same percentage.
 *
 * Rows are rendered in the order they're passed — callers sort upstream.
 */
const ContributionBreakdown = ({ title, totals, rows, emptyMessage, onRowClick }) => {
  if (!rows || rows.length === 0) {
    return (
      <section className="contrib">
        {title && <h2 className="contrib-title">{title}</h2>}
        <div className="contrib-empty">{emptyMessage || 'No data yet.'}</div>
      </section>
    );
  }

  return (
    <section className="contrib">
      {title && <h2 className="contrib-title">{title}</h2>}

      <div className="contrib-table" role="table">
        <div className="contrib-head" role="row">
          <div className="contrib-col-identity" role="columnheader">Source</div>
          {METRICS.map((m) => (
            <div key={m.key} className="contrib-col-metric" role="columnheader">
              <span className="contrib-col-metric-icon">{m.icon}</span>
              <span>{m.label}</span>
            </div>
          ))}
          {onRowClick && <div className="contrib-col-chev" role="columnheader" />}
        </div>

        {rows.map((row) => {
          const clickable = !!onRowClick;
          return (
            <div
              key={row.key}
              className={`contrib-row${clickable ? ' contrib-row-clickable' : ''}`}
              role="row"
              onClick={clickable ? () => onRowClick(row) : undefined}
            >
              <div className="contrib-cell-identity" role="cell">
                <div className="contrib-identity">{row.identity}</div>
                {row.meta && <div className="contrib-meta">{row.meta}</div>}
              </div>

              {METRICS.map((m) => {
                const value = row.stats?.[m.key] ?? 0;
                const share = pct(value, totals?.[m.key] ?? 0);
                return (
                  <div key={m.key} className="contrib-cell-metric" role="cell">
                    <div className="contrib-metric-values">
                      <span className="contrib-metric-value">{formatNumber(value)}</span>
                      <span className="contrib-metric-share">{formatPct(share)}</span>
                    </div>
                    <div
                      className="contrib-bar"
                      aria-label={`${formatPct(share)} of ${m.label.toLowerCase()}`}
                    >
                      <div
                        className="contrib-bar-fill"
                        style={{ width: `${Math.max(share, value > 0 ? 2 : 0)}%` }}
                      />
                    </div>
                  </div>
                );
              })}

              {clickable && (
                <div className="contrib-cell-chev" role="cell">
                  <IoChevronForward />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ContributionBreakdown;
